import { Box, Skeleton, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useEffect, useMemo, useRef } from "react";
import { isFetchBaseQueryError } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import {
  type ConfidenceRecallAtThresholds,
  type EvaluationDataset,
  type ExpectedPerformanceAtThresholds,
  type PerformanceRates,
  type RecallAtThresholds,
  type ReviewReason,
  type ReviewReasonContribution,
  type WindowedConfidenceRecallAtThresholds,
  useGetEventLabellingModelAlwaysOnFlagsQuery,
  useGetEventLabellingModelConfidenceRecallAtThresholdsQuery,
  useGetEventLabellingModelExpectedPerformanceAtThresholdsQuery,
  useGetEventLabellingModelRecallAtThresholdsQuery,
  useGetEventLabellingModelWindowedAlwaysOnFlagsQuery,
  useGetEventLabellingModelWindowedConfidenceRecallAtThresholdsQuery,
} from "store/api/generatedApi";
import {
  formatReviewReason,
  TARGETED_REVIEW_REASON_LABEL_KEYS,
} from "components/manualReview/utils/reviewReasons";
import { useFormatNumber } from "src/utils/formatNumber";

dayjs.extend(utc);

const formatAlwaysOnReasonLabel = (
  reason: ReviewReason,
  t: ReturnType<typeof useTenantTranslation>["t"]
): string => {
  const key = TARGETED_REVIEW_REASON_LABEL_KEYS[reason];
  return key ? t(key) : formatReviewReason(reason);
};

const isNotFound = (error: unknown): boolean =>
  isFetchBaseQueryError(error) && error.status === 404;

type RecallReadoutProps = Readonly<{
  tenantName: string;
  labellingName: string;
  materialThreshold: number | null;
  eventThreshold: number | null;
  areaThreshold: number | null;
}>;

type ConfidenceView =
  | Readonly<{
      kind: "windowed";
      data: WindowedConfidenceRecallAtThresholds;
    }>
  | Readonly<{
      kind: "legacy";
      data: ConfidenceRecallAtThresholds;
    }>;

type AlwaysOnView =
  | Readonly<{
      kind: "windowed";
      reasons: ReviewReasonContribution[];
      nPeriods: number;
    }>
  | Readonly<{
      kind: "legacy";
      reasons: ReviewReasonContribution[];
      nPeriods: number;
      nEvents: number;
    }>;

type Translation = ReturnType<typeof useTenantTranslation>["t"];
type CountFormatter = (value?: number | null) => string;

const DEBOUNCE_MS = 350;
const METRIC_WINDOW_DAYS = 30;

const WindowedConfidencePresentation = ({
  data,
  formatCount,
  formatMetricPercent,
  t,
}: Readonly<{
  data: WindowedConfidenceRecallAtThresholds;
  formatCount: CountFormatter;
  formatMetricPercent: (value: number) => string;
  t: Translation;
}>) => {
  if (
    data.residual_review_error_score == null ||
    data.base_review_error_score == null ||
    data.pure_miss_contribution_to_review_error_score == null
  ) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("recallNotEnoughWindowedData")}
      </Typography>
    );
  }

  return (
    <>
      <Typography variant="body2">
        <strong>
          {t("recallResidualReviewErrorScore", {
            score: formatMetricPercent(data.residual_review_error_score),
          })}
        </strong>
      </Typography>
      <Typography variant="body2">
        {t("recallPeriodWorkload", {
          flagged: formatCount(data.flagged_periods),
          total: formatCount(data.total_periods),
          percent:
            data.window_budget == null
              ? "—"
              : `${formatCount(data.window_budget * 100)}%`,
        })}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t("recallBaselineScoreWindow", {
          score: formatMetricPercent(data.base_review_error_score),
          contribution: formatMetricPercent(
            data.pure_miss_contribution_to_review_error_score
          ),
          days: formatCount(data.window_days),
        })}
      </Typography>
      {data.total_fn > 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("recallMissingDataTaggedFn", {
            tagged: formatCount(data.missing_data_tagged_fn),
            total: formatCount(data.total_fn),
          })}
        </Typography>
      )}
    </>
  );
};

const LegacyConfidencePresentation = ({
  data,
  formatCount,
  formatPercent,
  t,
}: Readonly<{
  data: ConfidenceRecallAtThresholds;
  formatCount: CountFormatter;
  formatPercent: (value: number | null) => string;
  t: Translation;
}>) => {
  if (data.total_errors === 0 || data.recall == null) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("recallNotEnoughData")}
      </Typography>
    );
  }

  const errorTypes = [
    {
      key: "recallCaughtFalsePositives" as const,
      caught: data.caught_false_positive,
      total: data.total_false_positive,
    },
    {
      key: "recallCaughtWrongMaterial" as const,
      caught: data.caught_wrong_material,
      total: data.total_wrong_material,
    },
    {
      key: "recallCaughtWrongArea" as const,
      caught: data.caught_wrong_area,
      total: data.total_wrong_area,
    },
  ];

  return (
    <>
      <Typography variant="body2">
        <strong>{formatPercent(data.recall)}</strong> {t("recallErrorsCaught")}{" "}
        {" · "}
        <strong>{formatPercent(data.window_budget)}</strong>{" "}
        {t("recallPeriodsReviewed")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t("recallCaughtHeader")}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {errorTypes
          .filter((errorType) => errorType.total > 0)
          .map((errorType) => (
            <Typography
              key={errorType.key}
              component="li"
              variant="body2"
              color="text.secondary"
            >
              {t(errorType.key, {
                caught: formatCount(errorType.caught),
                total: formatCount(errorType.total),
              })}
            </Typography>
          ))}
      </Box>
    </>
  );
};

export const RecallReadout = ({
  tenantName,
  labellingName,
  materialThreshold,
  eventThreshold,
  areaThreshold,
}: RecallReadoutProps) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const formatCount = formatNumber(0);
  const formatMetric = formatNumber(2);
  const formatPercent = (value: number | null): string =>
    value == null ? "—" : `≈${formatCount(value * 100)}%`;
  const formatMetricPercent = (value: number): string =>
    `≈${formatMetric(value * 100)}%`;

  const debouncedMaterial = useDebouncedValue(materialThreshold, DEBOUNCE_MS);
  const debouncedEvent = useDebouncedValue(eventThreshold, DEBOUNCE_MS);
  const debouncedArea = useDebouncedValue(areaThreshold, DEBOUNCE_MS);
  const allNull =
    debouncedMaterial == null &&
    debouncedEvent == null &&
    debouncedArea == null;
  const modelIdentity = `${tenantName}\u0000${labellingName}`;
  const rollbackRequiredModelIdentitiesRef = useRef<readonly string[]>([]);
  const requiresRollbackFallback =
    rollbackRequiredModelIdentitiesRef.current.includes(modelIdentity);

  const windowedConfidenceQuery =
    useGetEventLabellingModelWindowedConfidenceRecallAtThresholdsQuery(
      {
        tenantName,
        labellingName,
        windowDays: METRIC_WINDOW_DAYS,
        materialThreshold: debouncedMaterial,
        eventThreshold: debouncedEvent,
        areaThreshold: debouncedArea,
      },
      { skip: allNull || requiresRollbackFallback }
    );
  const windowedAlwaysOnQuery =
    useGetEventLabellingModelWindowedAlwaysOnFlagsQuery(
      {
        tenantName,
        labellingName,
        windowDays: METRIC_WINDOW_DAYS,
      },
      { skip: allNull || requiresRollbackFallback }
    );

  const windowedNotFound =
    isNotFound(windowedConfidenceQuery.error) ||
    isNotFound(windowedAlwaysOnQuery.error);
  const useRollbackFallback = requiresRollbackFallback || windowedNotFound;
  useEffect(() => {
    if (windowedNotFound && !requiresRollbackFallback) {
      rollbackRequiredModelIdentitiesRef.current = [
        ...rollbackRequiredModelIdentitiesRef.current,
        modelIdentity,
      ];
    }
  }, [modelIdentity, requiresRollbackFallback, windowedNotFound]);

  const confidenceFallbackQuery =
    useGetEventLabellingModelConfidenceRecallAtThresholdsQuery(
      {
        tenantName,
        labellingName,
        materialThreshold: debouncedMaterial,
        eventThreshold: debouncedEvent,
        areaThreshold: debouncedArea,
      },
      { skip: allNull || !useRollbackFallback }
    );
  const alwaysOnFallbackQuery = useGetEventLabellingModelAlwaysOnFlagsQuery(
    { tenantName, labellingName },
    { skip: allNull || !useRollbackFallback }
  );
  const expectedQuery =
    useGetEventLabellingModelExpectedPerformanceAtThresholdsQuery(
      {
        tenantName,
        labellingName,
        materialThreshold: debouncedMaterial,
        eventThreshold: debouncedEvent,
        areaThreshold: debouncedArea,
      },
      { skip: allNull }
    );

  const confidenceFallbackNotFound = isNotFound(confidenceFallbackQuery.error);
  const alwaysOnFallbackNotFound = isNotFound(alwaysOnFallbackQuery.error);
  const useLegacy =
    useRollbackFallback &&
    (confidenceFallbackNotFound || alwaysOnFallbackNotFound);
  const legacyQuery = useGetEventLabellingModelRecallAtThresholdsQuery(
    {
      tenantName,
      labellingName,
      materialThreshold: debouncedMaterial,
      eventThreshold: debouncedEvent,
      areaThreshold: debouncedArea,
    },
    { skip: allNull || !useLegacy }
  );

  const selectedLegacyConfidenceQuery = confidenceFallbackNotFound
    ? legacyQuery
    : confidenceFallbackQuery;
  const selectedConfidenceQuery = useRollbackFallback
    ? selectedLegacyConfidenceQuery
    : windowedConfidenceQuery;
  const confidenceCurrentView = useMemo<ConfidenceView | undefined>(() => {
    if (useRollbackFallback) {
      return selectedLegacyConfidenceQuery.currentData == null
        ? undefined
        : {
            kind: "legacy",
            data: selectedLegacyConfidenceQuery.currentData,
          };
    }
    return windowedConfidenceQuery.currentData == null
      ? undefined
      : { kind: "windowed", data: windowedConfidenceQuery.currentData };
  }, [
    selectedLegacyConfidenceQuery.currentData,
    useRollbackFallback,
    windowedConfidenceQuery.currentData,
  ]);
  const confidenceFetching = selectedConfidenceQuery.isFetching;
  const confidenceError = selectedConfidenceQuery.isError;
  const confidenceIdentity = `${modelIdentity}\u0000${
    useRollbackFallback ? "legacy" : "windowed"
  }`;
  const lastGoodConfidenceRef = useRef<
    { identity: string; view: ConfidenceView } | undefined
  >(undefined);

  useEffect(() => {
    if (!confidenceError && confidenceCurrentView != null) {
      lastGoodConfidenceRef.current = {
        identity: confidenceIdentity,
        view: confidenceCurrentView,
      };
    }
  }, [confidenceCurrentView, confidenceError, confidenceIdentity]);

  const confidenceView =
    confidenceCurrentView ??
    (!confidenceError &&
    lastGoodConfidenceRef.current?.identity === confidenceIdentity
      ? lastGoodConfidenceRef.current.view
      : undefined);
  const selectedLegacyAlwaysOnQuery = alwaysOnFallbackNotFound
    ? legacyQuery
    : alwaysOnFallbackQuery;
  const selectedAlwaysOnQuery = useRollbackFallback
    ? selectedLegacyAlwaysOnQuery
    : windowedAlwaysOnQuery;
  const alwaysOnView = useMemo<AlwaysOnView | undefined>(() => {
    if (!useRollbackFallback) {
      return windowedAlwaysOnQuery.currentData == null
        ? undefined
        : {
            kind: "windowed",
            reasons: windowedAlwaysOnQuery.currentData.reasons,
            nPeriods: windowedAlwaysOnQuery.currentData.n_periods,
          };
    }
    if (alwaysOnFallbackNotFound) {
      const legacyData:
        | Partial<
            Pick<
              RecallAtThresholds,
              "always_on_reasons" | "always_on_periods" | "always_on_events"
            >
          >
        | undefined = legacyQuery.currentData;
      return legacyData == null
        ? undefined
        : {
            kind: "legacy",
            reasons: legacyData.always_on_reasons ?? [],
            nPeriods: legacyData.always_on_periods ?? 0,
            nEvents: legacyData.always_on_events ?? 0,
          };
    }
    return alwaysOnFallbackQuery.currentData == null
      ? undefined
      : {
          kind: "legacy",
          reasons: alwaysOnFallbackQuery.currentData.reasons,
          nPeriods: alwaysOnFallbackQuery.currentData.n_periods,
          nEvents: alwaysOnFallbackQuery.currentData.n_events,
        };
  }, [
    alwaysOnFallbackNotFound,
    alwaysOnFallbackQuery.currentData,
    legacyQuery.currentData,
    useRollbackFallback,
    windowedAlwaysOnQuery.currentData,
  ]);
  const confidenceLoading = confidenceFetching && confidenceView == null;
  const alwaysOnLoading =
    selectedAlwaysOnQuery.isFetching && alwaysOnView == null;
  const alwaysOnError = selectedAlwaysOnQuery.isError;

  if (allNull) {
    return null;
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        maxWidth: 480,
      }}
    >
      {alwaysOnLoading && <Skeleton variant="text" width="50%" />}
      {alwaysOnError && (
        <Typography variant="body2" color="text.secondary">
          {t("recallAlwaysOnUnavailable")}
        </Typography>
      )}
      {alwaysOnView != null && alwaysOnView.reasons.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t("recallAlwaysOnHeader")}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {alwaysOnView.reasons.map((reasonContribution) => (
              <Typography
                key={reasonContribution.reason}
                component="li"
                variant="body2"
                color="text.secondary"
              >
                {t("recallAlwaysOnReasonPeriods", {
                  reason: formatAlwaysOnReasonLabel(
                    reasonContribution.reason,
                    t
                  ),
                  count: formatCount(reasonContribution.n_periods),
                })}
              </Typography>
            ))}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {alwaysOnView.kind === "legacy"
              ? t("recallAlwaysOnSummary", {
                  periods: formatCount(alwaysOnView.nPeriods),
                  events: formatCount(alwaysOnView.nEvents),
                })
              : t("recallAlwaysOnPeriodsSummary", {
                  periods: formatCount(alwaysOnView.nPeriods),
                })}
          </Typography>
        </Box>
      )}
      {confidenceLoading && (
        <Box>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="text" width="40%" />
        </Box>
      )}
      {confidenceError && (
        <Typography variant="body2" color="text.secondary">
          {t(
            useRollbackFallback
              ? "recallEstimateUnavailable"
              : "recallReviewErrorScoreUnavailable"
          )}
        </Typography>
      )}
      {!confidenceLoading &&
        !confidenceError &&
        confidenceView != null &&
        (confidenceView.kind === "windowed" ? (
          <WindowedConfidencePresentation
            data={confidenceView.data}
            formatCount={formatCount}
            formatMetricPercent={formatMetricPercent}
            t={t}
          />
        ) : (
          <LegacyConfidencePresentation
            data={confidenceView.data}
            formatCount={formatCount}
            formatPercent={formatPercent}
            t={t}
          />
        ))}
      <ExpectedPerformance
        data={expectedQuery.currentData}
        isError={expectedQuery.isError}
      />
    </Box>
  );
};

const RATE_KEYS = [
  { key: "jaccard", labelKey: "expectedPerformanceMetricJaccard" },
  { key: "fn_rate", labelKey: "expectedPerformanceMetricFalseNegative" },
  { key: "fp_rate", labelKey: "expectedPerformanceMetricFalsePositive" },
  { key: "material_accuracy", labelKey: "expectedPerformanceMetricMaterial" },
  { key: "area_accuracy", labelKey: "expectedPerformanceMetricArea" },
] as const;

const ExpectedPerformance = ({
  data,
  isError,
}: Readonly<{
  data: ExpectedPerformanceAtThresholds | undefined;
  isError: boolean;
}>) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const formatCount = formatNumber(0);
  const formatRate = (value: number | null | undefined): string =>
    value == null ? "—" : `${formatCount(value * 100)}%`;

  if (isError) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t("expectedPerformanceEstimateUnavailable")}
      </Typography>
    );
  }
  if (data == null) {
    return null;
  }
  if (data.dataset == null) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t("expectedPerformanceNoEvaluationDataset")}
      </Typography>
    );
  }
  if (data.flagged == null || data.all_periods == null) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t("expectedPerformanceNotEnoughData")}
        </Typography>
        <DatasetCaption dataset={data.dataset} />
      </Box>
    );
  }

  const rates = (source: PerformanceRates): string =>
    RATE_KEYS.map(
      ({ key, labelKey }) => `${t(labelKey)} ${formatRate(source[key])}`
    ).join(" · ");

  return (
    <Box sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
      <Typography variant="body2" color="text.secondary">
        {t("expectedPerformanceHeader")}
      </Typography>
      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
        {rates(data.flagged)} — {t("expectedPerformanceFlagged")}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontFamily: "monospace" }}
      >
        {rates(data.all_periods)} — {t("expectedPerformanceAllPeriods")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {t("expectedPerformanceExplainer")}
      </Typography>
      <DatasetCaption dataset={data.dataset} />
    </Box>
  );
};

const formatDay = (value: string): string =>
  dayjs.utc(value).format("YYYY-MM-DD");

const DatasetCaption = ({
  dataset,
}: Readonly<{ dataset: EvaluationDataset }>) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const formatCount = formatNumber(0);
  return (
    <>
      <Typography variant="caption" color="text.secondary" component="p">
        {t("expectedPerformanceDataset", {
          dataset: dataset.dataset_id,
          testFraction: formatCount(dataset.test_fraction * 100),
          dataStart: formatDay(dataset.data_start),
          dataEnd: formatDay(dataset.data_end),
          createdAt: formatDay(dataset.created_at),
          verifiedShare:
            dataset.verified_share == null
              ? "—"
              : formatCount(dataset.verified_share * 100),
        })}
      </Typography>
      {dataset.reference_start != null && dataset.reference_end != null && (
        <Typography variant="caption" color="text.secondary" component="p">
          {t("expectedPerformanceReferenceSpan", {
            referenceStart: formatDay(dataset.reference_start),
            referenceEnd: formatDay(dataset.reference_end),
          })}
        </Typography>
      )}
    </>
  );
};
